import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
  useTheme,
  Badge,
  CircularProgress,
  Chip
} from '@mui/material';

// Icons
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import LinkIcon from '@mui/icons-material/Link';
import ChatIcon from '@mui/icons-material/Chat';
import BarChartIcon from '@mui/icons-material/BarChart';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ScheduleIcon from '@mui/icons-material/Schedule';
import InventoryIcon from '@mui/icons-material/Inventory';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MapIcon from '@mui/icons-material/Map';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PeopleIcon from '@mui/icons-material/People';
import SearchIcon from '@mui/icons-material/Search';
import ContactsIcon from '@mui/icons-material/Contacts';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DescriptionIcon from '@mui/icons-material/Description';
import PaymentIcon from '@mui/icons-material/Payment';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PhoneIcon from '@mui/icons-material/Phone';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import LayersIcon from '@mui/icons-material/Layers';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import InsightsIcon from '@mui/icons-material/Insights';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SummarizeIcon from '@mui/icons-material/Summarize';

// Use auth context
import { useAuth } from '../../context/AuthContext';
import { BrandProvider } from '../../context/BrandContext';
import { SubsidiaryProvider } from '../../context/SubsidiaryContext';

// Module permissions
import { MODULES, hasModuleAccess, hasAnyModuleAccess } from '../../config/modules';

// Sidebar navigation — single source of truth (also drives the permission dialogs' captions)
import {
  CORE_NAV_ITEMS,
  INVENTORY_NAV_ITEMS,
  SHOPIFY_INVENTORY_VISIBLE_PATHS,
  PROCUREMENT_NAV_ITEMS,
  RETAIL_NAV_ITEMS,
  FINANCE_DIRECT_NAV_ITEMS,
  FINANCE_QB_NAV_ITEMS,
} from '../../config/navigation';

// Alert service for notifications
import { alertPreferencesService } from '../../services/alertPreferencesService';
import GlobalSearch from '../common/GlobalSearch';
import GlobalShortcuts from '../common/GlobalShortcuts';
import { ShortcutHelp } from '../ds';

const drawerWidth = 240;

// Icons for nav items, keyed by path (item data lives in config/navigation.js).
// A missing entry falls back to a default icon rather than breaking the sidebar.
const CORE_NAV_ICONS = {
  '/': <DashboardIcon />,
  '/operations': <DashboardIcon />,
  '/brands': <BusinessIcon />,
  '/connectors': <LinkIcon />,
  '/chat': <ChatIcon />,
  '/experts': <SmartToyIcon />,
  '/scheduled-jobs': <ScheduleIcon />,
  '/call-centre': <PhoneIcon />,
  '/contacts': <ContactsIcon />,
  '/admin': <AdminPanelSettingsIcon />,
  '/admin/audit-log': <AdminPanelSettingsIcon />,
};

const SUB_NAV_ICONS = {
  // Inventory
  '/inventory': <DashboardIcon fontSize="small" />,
  '/inventory/summary': <InsightsIcon fontSize="small" />,
  '/inventory/stock': <InventoryIcon fontSize="small" />,
  '/inventory/batches': <LayersIcon fontSize="small" />,
  '/inventory/movements': <SyncAltIcon fontSize="small" />,
  '/reports/inventory-master': <AssessmentIcon fontSize="small" />,
  '/reports/run-rates': <AssessmentIcon fontSize="small" />,
  '/inventory/cogs': <AttachMoneyIcon fontSize="small" />,
  '/inventory/boms': <AccountTreeIcon fontSize="small" />,
  '/inventory/packaging': <Inventory2Icon fontSize="small" />,
  '/inventory/work-orders': <PrecisionManufacturingIcon fontSize="small" />,
  '/inventory/cin7-unresolved': <ReportProblemOutlinedIcon fontSize="small" />,
  '/inventory/commercial-invoices': <AssessmentIcon fontSize="small" />,
  '/inventory/warehouses': <WarehouseIcon fontSize="small" />,
  '/inventory/warehouse-brand-subsidiary': <MapIcon fontSize="small" />,
  '/inventory/mappings': <MapIcon fontSize="small" />,
  '/inventory/transfers': <SwapHorizIcon fontSize="small" />,
  '/inventory/returns': <AssignmentReturnIcon fontSize="small" />,
  '/inventory/disposals': <DeleteSweepIcon fontSize="small" />,
  '/inventory/counts': <FactCheckIcon fontSize="small" />,
  '/inventory/trust-point-wizard': <FactCheckIcon fontSize="small" />,
  '/inventory/fba-capacity': <WarehouseIcon fontSize="small" />,
  '/inventory/fba-discrepancies': <WarningIcon fontSize="small" />,
  '/inventory/amazon-restock': <LocalShippingIcon fontSize="small" />,
  // Procurement
  '/procurement': <DashboardIcon fontSize="small" />,
  '/procurement/suppliers': <PeopleIcon fontSize="small" />,
  '/procurement/purchase-orders': <ShoppingCartIcon fontSize="small" />,
  '/procurement/goods-receipts': <LocalShippingIcon fontSize="small" />,
  '/procurement/invoices': <DescriptionIcon fontSize="small" />,
  '/procurement/payments': <PaymentIcon fontSize="small" />,
  // Retail
  '/retail': <DashboardIcon fontSize="small" />,
  '/retail/orders': <ReceiptIcon fontSize="small" />,
  '/inventory/price-tiers': <AttachMoneyIcon fontSize="small" />,
  '/retail/customers': <PeopleIcon fontSize="small" />,
  '/wholesale/pending-ship': <LocalShippingIcon fontSize="small" />,
  '/wholesale/ready-to-invoice': <ReceiptIcon fontSize="small" />,
  // Finance — direct entries
  '/finance/journal-reconciliation': <ReceiptIcon fontSize="small" />,
  '/finance/statements': <SummarizeIcon fontSize="small" />,
  '/finance/bank-gl-mappings': <AccountBalanceWalletIcon fontSize="small" />,
  '/subsidiaries': <BusinessIcon fontSize="small" />,
  '/finance/pl': <AssessmentIcon fontSize="small" />,
  '/finance/balance-sheet': <AssessmentIcon fontSize="small" />,
  '/finance/cash-flow': <AssessmentIcon fontSize="small" />,
  '/finance/cash-flow-projections': <TrendingUpIcon fontSize="small" />,
  '/finance/processor-balances': <AccountBalanceWalletIcon fontSize="small" />,
  '/finance/vat-return': <ReceiptIcon fontSize="small" />,
  '/finance/tax-returns': <ReceiptIcon fontSize="small" />,
  '/finance/vat-vendors': <PeopleIcon fontSize="small" />,
  '/finance/search': <SearchIcon fontSize="small" />,
  '/finance/periods': <ScheduleIcon fontSize="small" />,
  '/finance/data-sources': <LinkIcon fontSize="small" />,
  // Finance — QuickBooks submenu
  '/finance': <DashboardIcon fontSize="small" />,
  '/finance/cash': <AccountBalanceWalletIcon fontSize="small" />,
  '/finance/ar': <TrendingUpIcon fontSize="small" />,
  '/finance/credit-notes': <AssignmentReturnIcon fontSize="small" />,
  '/finance/ap': <TrendingDownIcon fontSize="small" />,
  '/finance/goods-in-transit': <TrendingUpIcon fontSize="small" />,
  '/finance/intercompany': <CompareArrowsIcon fontSize="small" />,
  '/finance/reconciliation': <SyncAltIcon fontSize="small" />,
  '/finance/settings': <SettingsIcon fontSize="small" />,
};

const withSubIcons = (items) =>
  items.map(item => ({ ...item, icon: SUB_NAV_ICONS[item.path] || <AssessmentIcon fontSize="small" /> }));

// Shared sidebar nav item. Renders the MUI ListItemButton as a real <a href> via
// React Router's Link (component={RouterLink}) so the browser's native context menu
// offers "Open in new tab", and Cmd/Ctrl+click and middle-click all work. Plain
// left-click is still intercepted by Link for client-side SPA navigation.
// onNavigate handles side effects only (e.g. closing the mobile drawer) — Link does
// the actual navigation, so it must NOT call navigate() itself.
function NavListItemButton({ to, onNavigate, selected, sx, children }) {
  return (
    <ListItemButton
      component={RouterLink}
      to={to}
      selected={selected}
      onClick={onNavigate}
      sx={sx}
    >
      {children}
    </ListItemButton>
  );
}

export default function Layout({ children }) {
  const { logout, user, permissions } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [inventoryOpen, setInventoryOpen] = useState(location.pathname.startsWith('/inventory'));
  const [procurementOpen, setProcurementOpen] = useState(location.pathname.startsWith('/procurement'));
  const [retailOpen, setRetailOpen] = useState(location.pathname.startsWith('/retail'));
  const [financeOpen, setFinanceOpen] = useState(location.pathname.startsWith('/finance') || location.pathname === '/subsidiaries');
  // QB-active is derived from the QB item list itself — a hand-maintained exemption list
  // rotted every time FINANCE_DIRECT_NAV_ITEMS grew, wrongly expanding the QuickBooks
  // group for own-ledger pages (the QB submenu is for QB raw data only).
  const isQbPath = (p) => FINANCE_QB_NAV_ITEMS.some(
    (i) => p === i.path || (i.path !== '/finance' && p.startsWith(i.path + '/'))
  );
  const [quickbooksOpen, setQuickbooksOpen] = useState(isQbPath(location.pathname));

  // Alert notification state
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingAlerts, setLoadingAlerts] = useState(false);

  const isMenuOpen = Boolean(anchorEl);
  const isNotificationMenuOpen = Boolean(notificationAnchorEl);

  // Fetch alerts and unread count
  const fetchAlerts = useCallback(async () => {
    try {
      setLoadingAlerts(true);
      const [alertsData, countData] = await Promise.all([
        alertPreferencesService.getAlerts({ limit: 10 }),
        alertPreferencesService.getUnreadCount()
      ]);
      setAlerts(alertsData.alerts || []);
      setUnreadCount(countData.count || 0);
    } catch (error) {
      console.error('Error fetching alerts:', error);
      // Don't show error to user - notifications are non-critical
    } finally {
      setLoadingAlerts(false);
    }
  }, []);

  // Fetch alerts on mount and every 60 seconds
  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  // Mark all alerts as read
  const handleMarkAllRead = async () => {
    try {
      await alertPreferencesService.markAllAlertsRead();
      setAlerts(prev => prev.map(a => ({ ...a, is_read: 1 })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Mark single alert as read
  const handleMarkAlertRead = async (alertId) => {
    try {
      await alertPreferencesService.markAlertsRead([alertId]);
      setAlerts(prev => prev.map(a =>
        a.id === alertId ? { ...a, is_read: 1 } : a
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking alert as read:', error);
    }
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  // Get severity color
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      default: return 'default';
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setNotificationAnchorEl(null);
  };

  // Sidebar items now navigate via React Router <Link> (see NavListItemButton),
  // so the click handler only needs to close the mobile drawer after a tap.
  const closeMobileDrawer = () => {
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  const isAdmin = user?.role === 'admin';

  const FEATURE_EXPERTS = (process.env.REACT_APP_FEATURE_EXPERTS ?? 'true') === 'true';

  const allMenuItems = CORE_NAV_ITEMS
    .filter(item => !item.adminOnly || isAdmin)
    .filter(item => item.feature !== 'experts' || FEATURE_EXPERTS)
    .map(item => ({ ...item, icon: CORE_NAV_ICONS[item.path] || <DashboardIcon /> }));

  // Filter menu items based on user permissions
  const menuItems = allMenuItems.filter(item => hasModuleAccess(permissions, item.moduleKey));

  // Inventory submenu items (includes Master Report)
  const allInventorySubItems = withSubIcons(INVENTORY_NAV_ITEMS);

  // For shopify inventory mode, only show simplified menu (no warehouse management)
  const isShopifyInventory = user?.inventorySource === 'shopify';
  const inventorySubItems = allInventorySubItems
    .filter(item => !isShopifyInventory || SHOPIFY_INVENTORY_VISIBLE_PATHS.includes(item.path))
    .filter(item => hasModuleAccess(permissions, item.moduleKey));

  const isInventoryActive = location.pathname.startsWith('/inventory') || location.pathname.startsWith('/reports/inventory') || location.pathname.startsWith('/reports/run-rates');
  const isProcurementActive = location.pathname.startsWith('/procurement');
  // Retail also owns the sales-side /wholesale/* routes (pending-ship,
  // ready-to-invoice, new) — they sit under the Retail section in the sidebar,
  // so the section header should light up on those too, not only /retail.
  const isRetailActive = location.pathname.startsWith('/retail') || location.pathname.startsWith('/wholesale');
  const isFinanceActive = location.pathname.startsWith('/finance') || location.pathname === '/subsidiaries';
  const isQuickBooksActive = isQbPath(location.pathname);

  const handleInventoryClick = () => {
    setInventoryOpen(!inventoryOpen);
  };

  const handleProcurementClick = () => {
    setProcurementOpen(!procurementOpen);
  };

  const handleRetailClick = () => {
    setRetailOpen(!retailOpen);
  };

  const handleFinanceClick = () => {
    setFinanceOpen(!financeOpen);
  };

  const handleQuickBooksClick = () => {
    setQuickbooksOpen(!quickbooksOpen);
  };

  // Procurement submenu items
  const allProcurementSubItems = withSubIcons(PROCUREMENT_NAV_ITEMS);

  const procurementSubItems = allProcurementSubItems.filter(item => hasModuleAccess(permissions, item.moduleKey));

  // Retail submenu items — DTC + wholesale (sales-side)
  const allRetailSubItems = withSubIcons(RETAIL_NAV_ITEMS);

  const retailSubItems = allRetailSubItems.filter(item => hasModuleAccess(permissions, item.moduleKey));

  // QuickBooks submenu items (all Finance items EXCEPT P&L Report)
  const allQuickBooksSubItems = withSubIcons(FINANCE_QB_NAV_ITEMS);

  const quickbooksSubItems = allQuickBooksSubItems.filter(item => hasModuleAccess(permissions, item.moduleKey));

  // Check if Finance section should be visible (user has access to QuickBooks, Journal Recon, Financial Statements, OR P&L)
  const hasFinanceAccess =
    quickbooksSubItems.length > 0 ||
    hasModuleAccess(permissions, MODULES.FINANCE_RECONCILIATION) ||
    hasModuleAccess(permissions, MODULES.FINANCE_STATEMENTS) ||
    hasModuleAccess(permissions, MODULES.FINANCE_PL);

  const drawer = (
    <div>
      <Toolbar sx={{ display: 'flex', alignItems: 'center', py: 2.5, minHeight: '64px !important' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
            background: 'var(--gradient-logo)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 800,
            fontSize: '1.125rem',
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
            textAlign: 'center',
            width: '100%',
            wordWrap: 'break-word',
          }}
        >
          SUPERCHARGEME
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
          <ListItem key={item.text} disablePadding>
            <NavListItemButton
              to={item.path}
              onNavigate={closeMobileDrawer}
              selected={isActive}
              sx={{
                borderRadius: '10px',
                mx: 1,
                mb: 0.25,
                py: 0.85,
                position: 'relative',
                transition: 'background-color 0.15s ease',
                '&:hover': {
                  backgroundColor: isActive ? 'var(--color-primary-a16)' : 'rgba(15, 15, 18, 0.04)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'var(--color-primary-a10)',
                  '&:hover': {
                    backgroundColor: 'var(--color-primary-a16)',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '22%',
                    bottom: '22%',
                    width: 3,
                    borderRadius: '0 3px 3px 0',
                    backgroundColor: 'var(--color-primary)',
                  },
                },
              }}
            >
              <ListItemIcon sx={{
                color: isActive ? 'primary.main' : 'text.secondary',
                minWidth: '40px'
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? 'primary.main' : 'text.primary',
                  letterSpacing: '-0.005em',
                }}
              />
            </NavListItemButton>
          </ListItem>
          );
        })}

        {/* Inventory Section with Submenu - Only show if user has access to at least one item */}
        {inventorySubItems.length > 0 && (
        <>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleInventoryClick}
            sx={{
              borderRadius: '8px',
              mx: 1,
              mb: 0.5,
              backgroundColor: isInventoryActive ? 'var(--color-primary-a08)' : 'transparent',
              '&:hover': {
                backgroundColor: isInventoryActive ? 'var(--color-primary-a16)' : 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <ListItemIcon sx={{ 
              color: isInventoryActive ? 'primary.main' : 'text.secondary',
              minWidth: '40px'
            }}>
              <InventoryIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Inventory" 
              primaryTypographyProps={{ 
                fontWeight: isInventoryActive ? '600' : '400',
                color: isInventoryActive ? 'text.primary' : 'text.secondary',
              }}
            />
            {inventoryOpen ? <ExpandLess color={isInventoryActive ? 'primary' : 'action'} /> : <ExpandMore color="action" />}
          </ListItemButton>
        </ListItem>
        <Collapse in={inventoryOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {inventorySubItems.map((subItem) => (
              <ListItem key={subItem.text} disablePadding>
                <NavListItemButton
                  to={subItem.path}
                  onNavigate={closeMobileDrawer}
                  selected={location.pathname === subItem.path}
                  sx={{
                    borderRadius: '8px',
                    mx: 1,
                    ml: 3,
                    mb: 0.5,
                    py: 0.75,
                    position: 'relative',
                    '&.Mui-selected': {
                      backgroundColor: 'var(--color-primary-a10)',
                      '&:hover': {
                        backgroundColor: 'var(--color-primary-a16)',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '22%',
                        bottom: '22%',
                        width: 3,
                        borderRadius: '0 3px 3px 0',
                        backgroundColor: 'var(--color-primary)',
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{
                    color: location.pathname === subItem.path ? 'primary.main' : 'text.secondary',
                    minWidth: '32px'
                  }}>
                    {subItem.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={subItem.text}
                    primaryTypographyProps={{
                      fontWeight: location.pathname === subItem.path ? '600' : '400',
                      color: location.pathname === subItem.path ? 'text.primary' : 'text.secondary',
                      fontSize: '0.875rem',
                    }}
                  />
                </NavListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
        </>
        )}

        {/* Procurement Section with Submenu - Only show if user has access to at least one item */}
        {procurementSubItems.length > 0 && (
        <>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleProcurementClick}
            sx={{
              borderRadius: '8px',
              mx: 1,
              mb: 0.5,
              backgroundColor: isProcurementActive ? 'var(--color-primary-a08)' : 'transparent',
              '&:hover': {
                backgroundColor: isProcurementActive ? 'var(--color-primary-a16)' : 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <ListItemIcon sx={{
              color: isProcurementActive ? 'primary.main' : 'text.secondary',
              minWidth: '40px'
            }}>
              <LocalShippingIcon />
            </ListItemIcon>
            <ListItemText
              primary="Procurement"
              primaryTypographyProps={{
                fontWeight: isProcurementActive ? '600' : '400',
                color: isProcurementActive ? 'text.primary' : 'text.secondary',
              }}
            />
            {procurementOpen ? <ExpandLess color={isProcurementActive ? 'primary' : 'action'} /> : <ExpandMore color="action" />}
          </ListItemButton>
        </ListItem>
        <Collapse in={procurementOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {procurementSubItems.map((subItem) => (
              <ListItem key={subItem.text} disablePadding>
                <NavListItemButton
                  to={subItem.path}
                  onNavigate={closeMobileDrawer}
                  selected={location.pathname === subItem.path}
                  sx={{
                    borderRadius: '8px',
                    mx: 1,
                    ml: 3,
                    mb: 0.5,
                    py: 0.75,
                    position: 'relative',
                    '&.Mui-selected': {
                      backgroundColor: 'var(--color-primary-a10)',
                      '&:hover': {
                        backgroundColor: 'var(--color-primary-a16)',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '22%',
                        bottom: '22%',
                        width: 3,
                        borderRadius: '0 3px 3px 0',
                        backgroundColor: 'var(--color-primary)',
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{
                    color: location.pathname === subItem.path ? 'primary.main' : 'text.secondary',
                    minWidth: '32px'
                  }}>
                    {subItem.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={subItem.text}
                    primaryTypographyProps={{
                      fontWeight: location.pathname === subItem.path ? '600' : '400',
                      color: location.pathname === subItem.path ? 'text.primary' : 'text.secondary',
                      fontSize: '0.875rem',
                    }}
                  />
                </NavListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
        </>
        )}

        {/* Retail Section with Submenu - Only show if user has access to at least one item */}
        {retailSubItems.length > 0 && (
        <>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleRetailClick}
            sx={{
              borderRadius: '8px',
              mx: 1,
              mb: 0.5,
              backgroundColor: isRetailActive ? 'var(--color-primary-a08)' : 'transparent',
              '&:hover': {
                backgroundColor: isRetailActive ? 'var(--color-primary-a16)' : 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <ListItemIcon sx={{
              color: isRetailActive ? 'primary.main' : 'text.secondary',
              minWidth: '40px'
            }}>
              <StorefrontIcon />
            </ListItemIcon>
            <ListItemText
              primary="Retail"
              primaryTypographyProps={{
                fontWeight: isRetailActive ? '600' : '400',
                color: isRetailActive ? 'text.primary' : 'text.secondary',
              }}
            />
            {retailOpen ? <ExpandLess color={isRetailActive ? 'primary' : 'action'} /> : <ExpandMore color="action" />}
          </ListItemButton>
        </ListItem>
        <Collapse in={retailOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {retailSubItems.map((subItem) => (
              <ListItem key={subItem.text} disablePadding>
                <NavListItemButton
                  to={subItem.path}
                  onNavigate={closeMobileDrawer}
                  selected={location.pathname === subItem.path}
                  sx={{
                    borderRadius: '8px',
                    mx: 1,
                    ml: 3,
                    mb: 0.5,
                    py: 0.75,
                    position: 'relative',
                    '&.Mui-selected': {
                      backgroundColor: 'var(--color-primary-a10)',
                      '&:hover': {
                        backgroundColor: 'var(--color-primary-a16)',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '22%',
                        bottom: '22%',
                        width: 3,
                        borderRadius: '0 3px 3px 0',
                        backgroundColor: 'var(--color-primary)',
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{
                    color: location.pathname === subItem.path ? 'primary.main' : 'text.secondary',
                    minWidth: '32px'
                  }}>
                    {subItem.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={subItem.text}
                    primaryTypographyProps={{
                      fontWeight: location.pathname === subItem.path ? '600' : '400',
                      color: location.pathname === subItem.path ? 'text.primary' : 'text.secondary',
                      fontSize: '0.875rem',
                    }}
                  />
                </NavListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
        </>
        )}

        {/* Finance Section - Only show if user has access to QuickBooks, Subsidiaries, or P&L */}
        {hasFinanceAccess && (
        <>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleFinanceClick}
            sx={{
              borderRadius: '8px',
              mx: 1,
              mb: 0.5,
              backgroundColor: isFinanceActive ? 'var(--color-primary-a08)' : 'transparent',
              '&:hover': {
                backgroundColor: isFinanceActive ? 'var(--color-primary-a16)' : 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <ListItemIcon sx={{
              color: isFinanceActive ? 'primary.main' : 'text.secondary',
              minWidth: '40px'
            }}>
              <AccountBalanceIcon />
            </ListItemIcon>
            <ListItemText
              primary="Finance"
              primaryTypographyProps={{
                fontWeight: isFinanceActive ? '600' : '400',
                color: isFinanceActive ? 'text.primary' : 'text.secondary',
              }}
            />
            {financeOpen ? <ExpandLess color={isFinanceActive ? 'primary' : 'action'} /> : <ExpandMore color="action" />}
          </ListItemButton>
        </ListItem>
        <Collapse in={financeOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>

            {/* Finance direct entries — declared in config/navigation.js (single source of truth) */}
            {FINANCE_DIRECT_NAV_ITEMS
              .filter(item => hasModuleAccess(permissions, item.moduleKey))
              .map(item => (
              <ListItem key={item.path} disablePadding>
                <NavListItemButton
                  to={item.path}
                  onNavigate={closeMobileDrawer}
                  selected={location.pathname === item.path}
                  sx={{
                    borderRadius: '8px',
                    mx: 1,
                    ml: 3,
                    mb: 0.5,
                    py: 0.75,
                    position: 'relative',
                    '&.Mui-selected': {
                      backgroundColor: 'var(--color-primary-a10)',
                      '&:hover': {
                        backgroundColor: 'var(--color-primary-a16)',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '22%',
                        bottom: '22%',
                        width: 3,
                        borderRadius: '0 3px 3px 0',
                        backgroundColor: 'var(--color-primary)',
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{
                    color: location.pathname === item.path ? 'primary.main' : 'text.secondary',
                    minWidth: '32px'
                  }}>
                    {SUB_NAV_ICONS[item.path] || <AssessmentIcon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: location.pathname === item.path ? '600' : '400',
                      color: location.pathname === item.path ? 'text.primary' : 'text.secondary',
                      fontSize: '0.875rem',
                    }}
                  />
                </NavListItemButton>
              </ListItem>
            ))}

            {/* QuickBooks - Only show if user has access to at least one QB item */}
            {quickbooksSubItems.length > 0 && (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={handleQuickBooksClick}
                    sx={{
                      borderRadius: '8px',
                      mx: 1,
                      ml: 3,
                      mb: 0.5,
                      py: 0.75,
                    position: 'relative',
                      backgroundColor: isQuickBooksActive ? 'var(--color-primary-a08)' : 'transparent',
                      '&:hover': {
                        backgroundColor: isQuickBooksActive ? 'var(--color-primary-a16)' : 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{
                      color: isQuickBooksActive ? 'primary.main' : 'text.secondary',
                      minWidth: '32px'
                    }}>
                      <AssessmentIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="QuickBooks"
                      primaryTypographyProps={{
                        fontWeight: isQuickBooksActive ? '600' : '400',
                        color: isQuickBooksActive ? 'text.primary' : 'text.secondary',
                        fontSize: '0.875rem',
                      }}
                    />
                    {quickbooksOpen ? <ExpandLess fontSize="small" color={isQuickBooksActive ? 'primary' : 'action'} /> : <ExpandMore fontSize="small" color="action" />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={quickbooksOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {quickbooksSubItems.map((subItem) => (
                      <ListItem key={subItem.text} disablePadding>
                        <NavListItemButton
                          to={subItem.path}
                          onNavigate={closeMobileDrawer}
                          selected={location.pathname === subItem.path}
                          sx={{
                            borderRadius: '8px',
                            mx: 1,
                            ml: 5,
                            mb: 0.5,
                            py: 0.75,
                            '&.Mui-selected': {
                      backgroundColor: 'var(--color-primary-a10)',
                      '&:hover': {
                        backgroundColor: 'var(--color-primary-a16)',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '22%',
                        bottom: '22%',
                        width: 3,
                        borderRadius: '0 3px 3px 0',
                        backgroundColor: 'var(--color-primary)',
                      },
                    },
                          }}
                        >
                          <ListItemIcon sx={{
                            color: location.pathname === subItem.path ? 'primary.main' : 'text.secondary',
                            minWidth: '28px'
                          }}>
                            {subItem.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={subItem.text}
                            primaryTypographyProps={{
                              fontWeight: location.pathname === subItem.path ? '600' : '400',
                              color: location.pathname === subItem.path ? 'text.primary' : 'text.secondary',
                              fontSize: '0.8125rem',
                            }}
                          />
                        </NavListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            )}

          </List>
        </Collapse>
        </>
        )}
      </List>
      <Divider sx={{ mt: 2 }} />
      <List>
        <ListItem disablePadding>
          <NavListItemButton
            to="/profile"
            onNavigate={closeMobileDrawer}
            selected={location.pathname === '/profile'}
            sx={{
              borderRadius: '8px',
              mx: 1,
              mb: 0.5,
              '&.Mui-selected': {
                      backgroundColor: 'var(--color-primary-a10)',
                      '&:hover': {
                        backgroundColor: 'var(--color-primary-a16)',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '22%',
                        bottom: '22%',
                        width: 3,
                        borderRadius: '0 3px 3px 0',
                        backgroundColor: 'var(--color-primary)',
                      },
                    },
            }}
          >
            <ListItemIcon sx={{
              color: location.pathname === '/profile' ? 'primary.main' : 'text.secondary',
              minWidth: '40px'
            }}>
              <AccountCircleIcon />
            </ListItemIcon>
            <ListItemText
              primary="Profile"
              primaryTypographyProps={{
                fontWeight: location.pathname === '/profile' ? '600' : '400',
                color: location.pathname === '/profile' ? 'text.primary' : 'text.secondary',
              }}
            />
          </NavListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleLogout}
            sx={{
              borderRadius: '8px',
              mx: 1,
              mb: 0.5,
            }}
          >
            <ListItemIcon sx={{ 
              color: 'text.secondary',
              minWidth: '40px'
            }}>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Logout" 
              primaryTypographyProps={{ 
                color: 'text.secondary',
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  const userMenu = (
    <Menu
      anchorEl={anchorEl}
      id="user-menu"
      keepMounted
      open={isMenuOpen}
      onClose={handleMenuClose}
      PaperProps={{
        elevation: 3,
        sx: {
          borderRadius: 2,
          minWidth: 180,
          mt: 1.5,
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <MenuItem onClick={() => { handleMenuClose(); navigate('/profile'); }}>
        <ListItemIcon>
          <AccountCircleIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Profile</ListItemText>
      </MenuItem>
      <MenuItem onClick={() => { handleMenuClose(); navigate('/settings'); }}>
        <ListItemIcon>
          <SettingsIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Settings</ListItemText>
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleLogout}>
        <ListItemIcon>
          <ExitToAppIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Logout</ListItemText>
      </MenuItem>
    </Menu>
  );

  const notificationMenu = (
    <Menu
      anchorEl={notificationAnchorEl}
      id="notification-menu"
      keepMounted
      open={isNotificationMenuOpen}
      onClose={handleMenuClose}
      PaperProps={{
        elevation: 3,
        sx: {
          borderRadius: 2,
          minWidth: 320,
          maxWidth: 380,
          maxHeight: 480,
          mt: 1.5,
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1" fontWeight="600">
          Alerts {unreadCount > 0 && <Chip label={unreadCount} size="small" color="primary" sx={{ ml: 1, height: 20 }} />}
        </Typography>
        {unreadCount > 0 && (
          <Typography
            variant="caption"
            color="primary"
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={handleMarkAllRead}
          >
            Mark all as read
          </Typography>
        )}
      </Box>
      <Divider />
      {loadingAlerts ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : alerts.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No alerts yet
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Configure your alert preferences in Settings
          </Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
          {alerts.map((alert) => (
            <Box
              key={alert.id}
              onClick={() => {
                if (!alert.is_read) {
                  handleMarkAlertRead(alert.id);
                }
              }}
              sx={{
                py: 1.5,
                px: 2,
                borderLeft: alert.is_read ? 'none' : '3px solid',
                borderLeftColor: alert.is_read ? 'transparent' : 'primary.main',
                backgroundColor: alert.is_read ? 'transparent' : 'action.hover',
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Chip
                    label={alert.severity}
                    size="small"
                    color={getSeverityColor(alert.severity)}
                    sx={{ height: 18, fontSize: '0.7rem' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {formatRelativeTime(alert.created_at)}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={alert.is_read ? 400 : 500} sx={{ mb: 0.5 }}>
                  {alert.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {alert.summary}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
      <Divider />
      <Box sx={{ p: 1.5, textAlign: 'center' }}>
        <Typography
          variant="body2"
          color="primary"
          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => {
            handleMenuClose();
            navigate('/settings/alerts');
          }}
        >
          View alert settings
        </Typography>
      </Box>
    </Menu>
  );

  // ?embed=1 mode: drop sidebar + header so the page can be iframed cleanly inside another tool
  // (e.g., the trust-point wizard embeds /inventory/movements). Pure visual change — no auth bypass.
  const isEmbed = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('embed') === '1';
  if (isEmbed) {
    return (
      <Box sx={{ p: 2 }}>
        <CssBaseline />
        {children}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'rgba(255, 255, 255, 0.82)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          color: 'text.primary',
          boxShadow: 'none',
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, md: 64 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              fontFamily: '"Plus Jakarta Sans", sans-serif',
              fontWeight: 700,
              fontSize: '1.05rem',
              letterSpacing: '-0.015em',
              color: 'text.primary',
            }}
          >
            {(() => {
              // Match against top-level + all submenu items so the AppBar
              // title reflects the current page even on /inventory/transfers,
              // /procurement/purchase-orders, /wholesale/new etc. Falls back
              // to 'Dashboard' only on the root or genuinely unknown paths.
              const all = [
                ...menuItems,
                ...inventorySubItems,
                ...procurementSubItems,
                ...retailSubItems,
                ...quickbooksSubItems,
              ];
              const exact = all.find(item => item.path === location.pathname);
              if (exact) return exact.text;
              // Prefix match for detail/edit pages: /inventory/transfers/123 →
              // "Transfers". Pick the MOST SPECIFIC (longest) matching path —
              // otherwise a section's own Overview (e.g. /inventory) shadows its
              // sub-pages (/inventory/transfers) since it appears first in the
              // list, and every detail/edit page wrongly reads "Overview".
              const prefix = all
                .filter(item => item.path !== '/' && location.pathname.startsWith(item.path + '/'))
                .sort((a, b) => b.path.length - a.path.length)[0];
              if (prefix) return prefix.text;
              return 'Dashboard';
            })()}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Help">
              <IconButton
                color="inherit"
                size="small"
                sx={{
                  width: 36, height: 36,
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'var(--color-primary-a08)',
                    color: 'var(--color-primary)',
                  },
                }}
              >
                <HelpOutlineIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Alerts">
              <IconButton
                color="inherit"
                size="small"
                onClick={handleNotificationMenuOpen}
                aria-controls={isNotificationMenuOpen ? 'notification-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={isNotificationMenuOpen ? 'true' : undefined}
                sx={{
                  width: 36, height: 36,
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'var(--color-primary-a08)',
                    color: 'var(--color-primary)',
                  },
                }}
              >
                <Badge badgeContent={unreadCount} color="error" max={99}>
                  <NotificationsIcon sx={{ fontSize: 20 }} />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title="Account">
              <IconButton
                onClick={handleProfileMenuOpen}
                aria-controls={isMenuOpen ? 'user-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={isMenuOpen ? 'true' : undefined}
                color="inherit"
                sx={{ ml: 0.5, p: 0.5 }}
              >
                <Avatar
                  alt={user?.fullName || 'User'}
                  sx={{
                    width: 32, height: 32,
                    background: 'var(--gradient-primary)',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#fff',
                  }}
                >
                  {user?.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
      
      {userMenu}
      {notificationMenu}
      
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: '#FCFCFD',
              borderRight: '1px solid rgba(15, 15, 18, 0.06)',
              boxShadow: 'none',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <Toolbar />
        <BrandProvider>
          <SubsidiaryProvider>
            {children}
          </SubsidiaryProvider>
        </BrandProvider>
      </Box>
      {/* Global Cmd+K search palette — listens for keyboard shortcut and
          renders modal across all routes. */}
      <GlobalSearch />
      {/* `?` opens shortcut help dialog; `g+letter` chord navigates. */}
      <ShortcutHelp />
      <GlobalShortcuts />
    </Box>
  );
}
